/**
 * Token Service - Integrates with Diet LLM Backend API
 * All token operations go through: https://diet-project-v1.onrender.com/api
 */

const API_BASE_URL = 'https://diet-project-v1.onrender.com/api';

class TokenService {
  constructor(apiBaseUrl = API_BASE_URL) {
    this.apiBaseUrl = apiBaseUrl;
  }

  /**
   * Get token balance for a user
   * @param {string} userId - User ID from Supabase Auth
   * @returns {Promise<number>} Token balance (0 if user has no balance)
   */
  async getBalance(userId) {
    if (!userId) {
      console.warn('No user ID provided to getBalance');
      return 0;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/tokens/balance/${userId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          // User has no balance yet - return 0
          return 0;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.balance || 0;
    } catch (error) {
      console.error('Error getting token balance:', error);
      return 0; // Graceful degradation
    }
  }

  /**
   * Get subscription status for a user
   * @param {string} userId - User ID from Supabase Auth
   * @returns {Promise<Object>} Subscription status object
   */
  async getSubscriptionStatus(userId) {
    if (!userId) {
      console.warn('No user ID provided to getSubscriptionStatus');
      return { has_subscription: false };
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/tokens/subscription/${userId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          // User has no subscription
          return { has_subscription: false };
        }
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { has_subscription: false }; // Graceful degradation
    }
  }

  /**
   * Purchase tokens
   * @param {string} userId - User ID from Supabase Auth
   * @param {string} packageId - Package ID: 'small', 'medium', or 'large'
   * @returns {Promise<string|null>} Checkout URL or null on error
   */
  async purchaseTokens(userId, packageId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!['small', 'medium', 'large'].includes(packageId)) {
      throw new Error('Invalid package ID. Must be: small, medium, or large');
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/tokens/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          package_id: packageId,
          subscription: false
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.checkout_url || null;
    } catch (error) {
      console.error('Error purchasing tokens:', error);
      throw error;
    }
  }

  /**
   * Create a subscription
   * @param {string} userId - User ID from Supabase Auth
   * @returns {Promise<string|null>} Checkout URL or null on error
   */
  async subscribe(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/tokens/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.checkout_url || null;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const tokenService = new TokenService();

// Export class for testing or custom instances
export default TokenService;
